import { useCallback, useEffect, useReducer, useRef } from 'react';
import { chatReducer, historyOf, requestHistoryOf, initialChatState } from './chatReducer.js';
import { parseSseStream } from './sse.js';
function failureMessage(status) {
    if (status === 409)
        return 'Another turn is still running. Wait a moment and try again.';
    if (status === 403)
        return 'Your session needs a refresh. Reload the page and try again.';
    if (status === 401)
        return 'Your session has expired. Sign in again.';
    return `request failed with status ${status}`;
}
export function useAgentChat(opts) {
    const { send } = opts;
    const [state, dispatch] = useReducer(chatReducer, initialChatState);
    const stateRef = useRef(state);
    const abortRef = useRef(null);
    // Every action goes through the ref first, so `reset(); submit(q)` in one
    // handler reads the fresh state instead of the last render's.
    const apply = useCallback((action) => {
        stateRef.current = chatReducer(stateRef.current, action);
        dispatch(action);
    }, []);
    const submit = useCallback(async (text) => {
        const trimmed = text.trim();
        if (!trimmed || stateRef.current.status === 'streaming')
            return;
        const messages = requestHistoryOf([...historyOf(stateRef.current), { role: 'user', content: trimmed }]);
        const controller = new AbortController();
        abortRef.current = controller;
        // A superseded run (after `reset`) must not write into the fresh state.
        const live = () => abortRef.current === controller;
        apply({ type: 'submit', text: trimmed });
        const seen = { done: false, error: false };
        try {
            const response = await send({ messages, signal: controller.signal });
            // A transport that ignores `signal` still ends a cancelled run as "cancelled".
            controller.signal.throwIfAborted();
            if (!response.ok || !response.body) {
                if (live())
                    apply({ type: 'fail', message: failureMessage(response.status) });
                return;
            }
            for await (const event of parseSseStream(response.body)) {
                controller.signal.throwIfAborted();
                if (!live())
                    return;
                apply({ type: 'event', event });
                if (event.type === 'done')
                    seen.done = true;
                if (event.type === 'error') {
                    seen.error = true;
                    break;
                }
            }
            controller.signal.throwIfAborted();
            if (seen.error || !live())
                return;
            if (!seen.done) {
                apply({ type: 'fail', message: 'stream ended early' });
                return;
            }
            apply({ type: 'complete' });
        }
        catch (err) {
            if (!live())
                return;
            const message = controller.signal.aborted ? 'cancelled' : err instanceof Error ? err.message : String(err);
            apply({ type: 'fail', message });
        }
        finally {
            if (live())
                abortRef.current = null;
        }
    }, [send, apply]);
    useEffect(() => () => {
        const controller = abortRef.current;
        abortRef.current = null;
        controller?.abort();
    }, []);
    const cancel = useCallback(() => abortRef.current?.abort(), []);
    const reset = useCallback(() => {
        const controller = abortRef.current;
        abortRef.current = null;
        controller?.abort();
        apply({ type: 'reset' });
    }, [apply]);
    return { ...state, submit, cancel, reset };
}
//# sourceMappingURL=useAgentChat.js.map